import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  numberAttribute,
  output
} from '@angular/core';
import { MngMessageService } from '@mng/mng-message-handler';
import { IFile, MultiUpload } from './mng-file.types';
import { MngFileSizePipe } from './mng-filesize.pipe';
import { MatIcon } from '@angular/material/icon';
import { LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { FileDropComponent } from './mng-file-drop.component';
// TODO: make a FormControl with ControlValueAccessor:
// https://gist.github.com/Maks3w/8a02cc0f7745335695f3,
// https://stackoverflow.com/questions/41889384/angular2-validation-for-input-type-file-wont-trigger-when-changing-the-fi/41938495#41938495
@Component({
  selector: 'mng-file-select',
  imports: [FileDropComponent, LayoutDirective, LayoutAlignDirective, MatIcon, MngFileSizePipe],
  templateUrl: './mng-file-select.component.html',
  styleUrl: './mng-file-select.component.scss',
  providers: [MngFileSizePipe],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MngFileSelectComponent implements AfterViewInit {
  private readonly messageHandler = inject(MngMessageService);
  private readonly fileSizePipe = inject(MngFileSizePipe);
  static counter = 0;

  readonly filesSelected = output<MultiUpload>();
  readonly fileSelected = output<IFile>();
  @Input() base64Handler: (base64: string, file: IFile) => void;
  @Input() accept = '*/*';
  @Input({ transform: booleanAttribute }) multiple = false;
  @Input({ transform: numberAttribute }) maxFilesize = 1024 * 1024 * 10;
  @Input({ transform: numberAttribute }) maxTotalSize = 1024 * 1024 * 100;

  @Input({ transform: booleanAttribute }) disableFilesizeHint = false;
  @Input() supportedFilesText: string;
  @Input() fileText: string;

  fileInputElement: HTMLInputElement;
  elementId: string;

  constructor() {
    MngFileSelectComponent.counter++;
    this.elementId = `file-input_${MngFileSelectComponent.counter}`;
  }

  ngAfterViewInit() {
    this.fileInputElement = document.getElementById(this.elementId) as HTMLInputElement;
    this.fileInputElement.addEventListener('change', () => {
      const files = this.fileInputElement['files'];
      this.handleFiles(Array.from(files));
    });
  }

  handleFiles(files: File[]) {
    let totalSize = 0;
    const result: IFile[] = [];
    for (const file of files) {
      if (!this.isAcceptedFileType(file)) {
        this.messageHandler.showMessage(
          `Der Dateityp der Datei "${file.name}" wird nicht unterstützt.`,
          this.messageHandler.createDefaultSnackbarErrorConfig(8000)
        );
        continue;
      }

      if (file.size > this.maxFilesize) {
        this.messageHandler.showMessage(
          `Die Datei "${file.name}" ist zu groß (${this.fileSizePipe.transform(file.size)}). Maximal erlaubt sind ${this.fileSizePipe.transform(this.maxFilesize)} pro Datei.`,
          this.messageHandler.createDefaultSnackbarErrorConfig(8000)
        );
        continue;
      }

      totalSize += file.size;
      let base64 = '';
      const currentFile: IFile = {
        name: file.name,
        size: file.size,
        type: file.type,
        file: file
      };
      if (this.base64Handler) {
        const fileReader = new FileReader();
        fileReader.onload = (fileLoadedEvent) => {
          base64 = <string>fileLoadedEvent.target['result'];
          this.base64Handler(base64, currentFile);
        };
        fileReader.readAsDataURL(file);
      }
      result.push(currentFile);
    }

    if (totalSize > this.maxTotalSize) {
      this.messageHandler.showMessage(
        `Die Gesamtgröße aller Dateien (${this.fileSizePipe.transform(totalSize)}) überschreitet das Maximum von ${this.fileSizePipe.transform(this.maxTotalSize)}.`,
        this.messageHandler.createDefaultSnackbarErrorConfig(8000)
      );
      return;
    } else {
      if (this.multiple) {
        this.filesSelected.emit({ files: result, totalSize: totalSize });
      } else {
        this.fileSelected.emit(result[0]);
      }
    }
    this.fileInputElement.value = '';
  }

  onFileDropped(event: File[]) {
    this.handleFiles(event);
  }

  selectFileInput() {
    this.fileInputElement.click();
  }

  private isAcceptedFileType(file: File): boolean {
    const acceptedTypes = this.accept.split(',').map((t) => t.trim());

    return acceptedTypes.some((type) => {
      if (type.startsWith('.')) {
        // Extension match (e.g., .pdf)
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      } else if (type.endsWith('/*')) {
        // Wildcard match (e.g., image/*)
        const baseType = type.split('/')[0];
        return file.type.startsWith(baseType + '/');
      } else {
        // Exact mime match (e.g., image/png)
        return file.type === type;
      }
    });
  }
}
